import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { environment } from '@environment/environment';
import { EnvironmentsService } from '@configuration/shared/services/environments.service';
import { IAuthentication } from '@shared/interfaces/authentication.interface';
import { IHiringFirm } from '@shared/interfaces/hiring-firm.interface';
import { IPayload } from '@shared/interfaces/payload.interface';
import { ISetting } from '@shared/interfaces/setting.interface';
import { IUser } from '@shared/interfaces/user.interface';
import { AnnouncementsService } from '@shared/services/announcements.service';
import { AuthService } from '@shared/services/auth.service';
import { CommonEnvironmentsService } from '@shared/services/environments.service';
import { HiringFirmsService } from '@shared/services/hiring-firms.service';
import { LanguageService } from '@shared/services/language.service';
import { LoggerService } from '@shared/services/logger.service';
import { SidebarService } from '@shared/services/sidebar.service';
import { ToastService } from '@shared/services/toast.service';
import { UpdatesService } from '@shared/services/updates.service';
import { UsersService } from '@shared/services/users.service';
import { jwtDecode } from 'jwt-decode';
import { LocalStorage, SessionStorage } from 'ngx-webstorage';
import { forkJoin, Subscription } from 'rxjs';

@Component({
    selector: 'app-topbar',
    templateUrl: './topbar.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ConfigurationTopbarComponent implements OnInit, OnDestroy {
  @SessionStorage() public hiringFirm: IHiringFirm;
  @SessionStorage() public settings: ISetting;
  @SessionStorage() public user: IUser;

  @LocalStorage() public isSubAccount: boolean;
  @LocalStorage() public canManageSubAccount: boolean;

  public ENDPOINTS_ASSETS: string = environment.ENDPOINTS.ASSETS;

  public isWhiteLabelEnabled = Boolean(false);

  public apps: any[] = [];

  public companies: IHiringFirm[];

  public currentLanguage: string;
  public languages = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
    { code: 'fr', label: 'Français' },
    { code: 'fr-ca', label: 'Français (Canada)' }
  ];

  private readonly constructorName: string = String(this.constructor.name);

  private reloadDataSetSubscription: Subscription;

  constructor(
    private readonly _announcements: AnnouncementsService,
    private readonly _auth: AuthService,
    private readonly _commonEnvironments: CommonEnvironmentsService,
    private readonly _environments: EnvironmentsService,
    private readonly _hiringFirms: HiringFirmsService,
    private readonly _language: LanguageService,
    private readonly _logger: LoggerService,
    private readonly _sidebar: SidebarService,
    private readonly _toast: ToastService,
    private readonly _users: UsersService,
    private readonly _updates: UpdatesService
  ) {
    const url = 'Reload sub account dataset';
    this.reloadDataSetSubscription =
      this._sidebar.reloadDatasetCalled$.subscribe(
        (res: any) => {
          this._logger.info(this.constructorName, url, res);

          this.setApps();
          this.getUsersCompanies();
        },
        (err: any) => this._logger.error(this.constructorName, url, err)
      );
  }

  ngOnInit(): void {
    this.isWhiteLabelEnabled =
      this.settings?.is_plugins_white_label_enabled || false;

    if (this.user?.id) {
      this._announcements.startPolling();
    }

    this.currentLanguage = this._environments.lng || 'en';

    this.setApps();
    this.getUsersCompanies();
  }

  ngOnDestroy(): void {
    this.reloadDataSetSubscription?.unsubscribe();
  }

  public goTo(a: any) {
    if (!a.isDisabled) {
      window.open(a.url, '_blank');
    }
  }

  public changeLanguage(langCode: string) {
    this.currentLanguage = langCode;

    const url = `PATCH /users/${this.user.id}`;

    this._language
      .persistUserLanguage(this.user, this.hiringFirm, langCode)
      .subscribe(
        (res: IUser) => {
          this._logger.info(this.constructorName, url, res);
          this.user = res;

          // Reload so the new language is applied across the whole app shell.
          this._language.reloadPage();
        },
        (err: any) => this._logger.error(this.constructorName, url, err)
      );
  }

  public onChangeCompanies(id: string) {
    if (this.hiringFirm.id !== id) {
      this.hiringFirm = this.companies.find((c: IHiringFirm) => c.id === id);

      this.settings = this.hiringFirm?.settings;

      const url = 'POST /oauth/refresh';
      this._auth
        .postRefresh({
          data: {
            attributes: {
              hiring_firm_id: this.hiringFirm.id
            }
          }
        })
        .subscribe(
          (res: IAuthentication) => {
            this._logger.info(this.constructorName, url, res);

            const newToken = res.token;
            if (!!newToken) {
              this._commonEnvironments.setToken('recruiter-token', newToken);

              const payload: IPayload = jwtDecode(newToken);
              this.isSubAccount = payload.is_sub_account;
              this.canManageSubAccount = !!payload.recruiter_id;

              forkJoin([
                this._users.get(),
                this._hiringFirms.getReferenceRelationships(
                  this.hiringFirm.id,
                  true
                )
              ]).subscribe(
                (resUser: any[]) => {
                  this._logger.info(this.constructorName, url, resUser);

                  this._sidebar.reloadDatasetSource.next();
                },
                (err: any) => {
                  this._logger.error(this.constructorName, url, err);
                }
              );
            }
          },
          (err: any) => {
            this._logger.error(this.constructorName, url, err);

            this._toast.error('TOASTS.COMPANY_SWITCH_FAILED');
          }
        );
    }
  }

  private getUsersCompanies() {
    const id = this.user?.id;

    const url = `GET /users/${id}/hiring_firms`;
    this._users.getHiringFirms(id).subscribe(
      (res: IHiringFirm[]) => {
        this._logger.info(this.constructorName, url, res);

        this.companies = res;
      },
      (err: any) => this._logger.error(this.constructorName, url, err)
    );
  }

  private setApps() {
    this.apps = this._commonEnvironments.APPS.filter(
      (a: any) => a.label !== 'configuration'
    );

    if (
      !this.settings?.is_plugins_candidate_feedback_enabled &&
      !this.settings?.is_plugins_recruiter_feedback_enabled &&
      !this.settings?.is_plugins_pre_screening_enabled
    ) {
      this.apps = this.apps.filter((a: any) => a.label !== 'feedback');
    }

    if (this.isWhiteLabelEnabled) {
      if (!!this.settings.white_label_custom_analytics_domain) {
        const customUrl = this.settings.white_label_custom_analytics_domain;
        this.apps = this.apps.map((a: any) =>
          a.label === 'analytics' ? {...a, url: customUrl} : a
        );
      }
  
      if (!!this.settings.domain_name) {
        const customUrl = this.settings.domain_name;
        this.apps = this.apps.map((a: any) =>
          a.label === 'references' ? {...a, url: customUrl} : a
        );
      }
    }
  }
}
